<template lang="pug">
  .b-project-tile(:class="{ archived: project.isArchived, yellow: project.isTemplate }")
    ._labels
      ._label.-green(v-if="access.isProjectAuthor(true)") Мой проект
      ._label.-yellow(v-if="project.isTemplate") Шаблон
    ._top
      router-link._title(:to="{ name: 'project-stages', params: { projectId: project.id } }") {{ project.name }}
      ._actions
        NotificationCount(:count="notificationCount")
        Bookmark(category="project" :id="project['@id']")
        EllipsisMenu(:items="menuItems")
    address._address
      Icon(name="map")
      | {{ project.objectAddress }}
    ._middle
      UserTile._author(:user="project.author" label="Руководитель")
      .b-company-tile-sm._company(v-if="project.companyDepartment")
        AvatarEditable._avatar(:avatar="project.companyDepartment.company.avatar" :editable="false" placeholder="no-avatar-project")
        router-link(:to="{ name: 'company-departments', params: { companyId: project.companyDepartment.company.id } }")._company {{ project.companyDepartment.company.name }}
        ._department {{ project.companyDepartment.name }}
      ._params
        ._param(v-if="userCanEdit && project.pricePerSquareMeter")
          Icon._param-icon.-purple(name="price")
          ._param-desc Цена <span>{{ project.pricePerSquareMeter }} ₽/м<sup>2</sup></span>
        ._param(v-if="project.objectArea")
          Icon._param-icon.-turquoise(name="square2")
          ._param-desc Площадь <span>{{ project.objectArea }} м<sup>2</sup></span>
    BaseProgressbar._progressbar(v-bind="taskProgress")
    BaseProgressbar._progressbar(v-bind="dateProgress")

</template>

<script>
import Access from '@/utils/access';
import dateProgress from '@/utils/dateProgress';
import taskProgress from '@/utils/taskProgress';

import BaseProgressbar from '@/components/BaseProgressbar.vue';
import CommonDropdown from '@/components/CommonDropdown.vue';
import UsersTile from '@/components/UsersTile.vue';
import NotificationCount from '@/components/base/NotificationCount.vue';
import UserTile from '@/components/UserTile.vue';
import EllipsisMenu from '@/components/EllipsisMenu.vue';
import AvatarEditable from '@/components/AvatarEditable.vue';

export default {
  name: 'ProjectTile',
  components: { AvatarEditable, EllipsisMenu, UserTile, UsersTile, CommonDropdown, BaseProgressbar, NotificationCount }, // eslint-disable-line
  props: {
    project: {
      type: Object,
      required: true,
    },
    menuItems: Array,
  },
  computed: {
    notificationCount() {
      const getter = this.$store.getters['notification/itemIdsByFilter'];
      return getter({ projectId: this.project.id }).length;
    },
    dateProgress() {
      return dateProgress(this.project.dateStart, this.project.dateFinish);
    },
    taskProgress() {
      return taskProgress(this.project.numberOfTasks, this.project.numberOfClosedTasks);
    },
    access() { return new Access(this.project, this.$store.state.auth.user); },
    userCanEdit() { return this.access.isProjectEditable && (this.access.isProjectAuthor() || this.access.isProjectAdmin()); },
  },
};
</script>

<style scoped lang="scss">
.company-tile-sm {
  display: grid;
  grid-template-columns: 40px auto;
  column-gap: 15px;
  margin-bottom: 10px;

  &__avatar {
    width: 40px;
    height: 40px;
    border-width: 2px;
    border-radius: 16px;
    grid-row: 1 / 2 span;
  }

  &__company {
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
  }

  &__department {
    font-size: 13px;
    font-weight: 400;
  }
}

.project-tile {
  grid-column: 4 span;
  padding: 45px 32px;
  background-color: var(--color-white);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  background-image: url(../assets/img/svg/decorate.svg);
  background-repeat: no-repeat;
  background-size: 100%;
  background-position: left bottom;
  color: var(--color-super-dark);
  position: relative;

  &.yellow {
    background-image: url(../assets/img/svg/decorate2.svg);
  }

  &.archived {
    filter: grayscale(100%);
  }

  &__labels {
    position: absolute;
    top: -10px;
    left: 32px;
    display: flex;
    gap: 10px;
  }

  &__label {
    font-size: 12px;
    display: flex;
    align-items: center;
    text-align: center;
    letter-spacing: 0.03em;
    color: #FFFFFF;
    border-radius: 4px;
    padding: 0 14px;
    box-sizing: border-box;
    height: 20px;

    &--green { background: #5AC169; }
    &--yellow { background: #F9D208; }
  }

  &__top {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 32px;
  }

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  &__title {
    font-size: 18px;
    line-height: 24px;
    transition: color 0.3s;
    margin-right: auto;
    &:hover {
      color: var(--color-hover);
    }
  }

  &__address {
    display: flex;
    margin-bottom: 24px;
    color: var(--color-middle-gray);
    font-style: normal;
    font-size: 14px;

    svg {
      display: block;
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      padding-right: 4px;
      fill: var(--color-middle-gray);
    }
  }

  &__params {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
  }

  &__param {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 28px;
    &:not(:last-child) {
      border-right: 1px solid var(--color-super-light);
    }
  }

  &__param-icon {
    display: block;
    width: 27px;
    height: 27px;
    border: 7px solid;
    border-radius: 5px;
    margin-bottom: 11px;

    &--purple {
      border-color: var(--color-purple);
      background-color: var(--color-purple);
    }

    &--turquoise {
      border-color: var(--color-purple2);
      background-color: var(--color-purple2);
    }
  }

  &__param-desc {
    font-size: 13px;
    font-weight: 400;
    white-space: nowrap;
    color: var(--color-middle-gray);
    span {
      font-weight: 700;
      color: var(--color-dark-gray);
    }
  }

  &__author {
    position: relative;
    margin-bottom: 30px;
  }

  &__members {
    margin-bottom: 40px;
  }

  &__progressbar {
    margin-bottom: 12px;

    &:not(:last-child) {
      margin-bottom: 24px;
    }
  }

  @media (max-width: 1024px) {
    &__members {
      margin-bottom: 28px;
    }
  }

  @media (max-width: 768px) {
    &__top {
      margin-bottom: 12px;
    }

    &__title {
      font-size: 16px;
      line-height: 24px;

    }
    &__address {
      margin-bottom: 18px;
      font-size: 12px;
    }
  }

  @media (max-width: 768px) and (min-width:500px) {
    &__middle {
      display: grid;
      grid-template-columns: 1fr 2fr;
      grid-template-rows: auto;
      grid-template-areas:
        "params author"
        "params members"
    }

    &__author {
      grid-area: author;
    }

    &__members {
      grid-area: members;
    }

    &__params {
      grid-area: params;
      flex-direction: column;
      align-items: flex-start;
    }

    &__param {
      border: 0 !important;
      padding: 0;
      display: flex;
      flex-direction: row;

      &:first-child {
        margin-bottom: 15px;
      }
    }

    &__param-icon {
      margin: 0 13px 0 0;
      width: 32px;
      height: 32px;
    }

    &__param-desc {
      font-size: 12px;
      line-height: 15px;
      span {
        display: block;
        margin-top: 2px;
      }
    }
  }
}
</style>
